import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import {
  clearVerificationEmailError,
  clearVerificationEmailFields,
  resetVerificationEmailRequestState,
  setEmail,
  setVerificationEmailCode
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { verificationEmail } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { VerificationEmailError } from '../../../../store/features/user-access/user-registration/user-registration-types';
import { messagesForVerificationEmailError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';

import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';

export interface EmailVerificationPageLogicCallbacks {
  buttonDisabled: boolean;
  blockButton: boolean;
  openDialogAccordoQuadro: boolean;
  email: string;
  verificationEmailCode: string;

  verificationEmailRequestStatus: ApiRequestStatus;
  verificationEmailError: VerificationEmailError;

  updateVerificationEmailCode: (code?: string) => void;
  handleClose: () => void;
  handleSubmit: () => void;
  handleCloseAccordoQuadroError: () => void;
}

export const useEmailVerificationPageLogic = (
  actions: GlobalActions
): EmailVerificationPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const [openDialogAccordoQuadro, setOpenDialogAccordoQuadro] = useState(false)
  const [blockButton,setBlockButton] = useState(false)
  const {
    email,
    verificationEmailCode,
    verificationEmailRequestStatus,
    verificationEmailError,
  } = useAppSelector((state) => state.features.userRegistration);

  const [buttonDisabled, setButtonDisabled] = useState(
    !verificationEmailCode || verificationEmailCode?.length === 0
  );

  const updateVerificationEmailCode = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setVerificationEmailCode(value));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    dispatch(verificationEmail());
  }, [dispatch]);

  useEffect(() => {
    if (verificationEmailRequestStatus === 'succeeded') {
      dispatch(resetVerificationEmailRequestState());
      navigate(appRoutes.InsertPhoneNumberUserRegistration, {
        replace: false,
      });
    }
  }, [dispatch, navigate, verificationEmailRequestStatus]);

  useEffect(() => {
    if (verificationEmailError) {
      if(verificationEmailError?.type === 'TROPPI_TENTATIVI'){
        dispatch(setEmail(''))
      }
      if (verificationEmailError?.type === 'ACCORDO_QUADRO_NON_ATTIVO' && !blockButton){
        setBlockButton(true)
        setOpenDialogAccordoQuadro(true)
      }
      if (
        verificationEmailError?.type === 'GENERIC_ERROR' ||
        verificationEmailError?.type === 'CODICE_DI_VERIFICA_SBAGLIATO'
      ) {
        const errorMessages = messagesForVerificationEmailError(
          verificationEmailError,
          intl
        );
        showToast({
          type: 'error',
          title: errorMessages.title,
          description: errorMessages.description,
          close: closeToast,
        });
        dispatch(clearVerificationEmailError());
      }
    }
  }, [
    intl,
    verificationEmailError,
    dispatch,
    formatMessage,
    showToast,
    closeToast,
  ]);

  const handleClose = useCallback(() => {
    dispatch(resetVerificationEmailRequestState());
    dispatch(clearVerificationEmailFields());
    navigate(appRoutes.InsertEmailUserRegistration, { replace: true });
  }, [dispatch, navigate]);

  const handleCloseAccordoQuadroError = ()=>{
    setOpenDialogAccordoQuadro(false)
  }
  return {
    buttonDisabled,
    email,
    verificationEmailCode,
    verificationEmailRequestStatus,
    verificationEmailError,
    updateVerificationEmailCode,
    handleClose,
    handleSubmit,
    openDialogAccordoQuadro,
    blockButton,
    handleCloseAccordoQuadroError
  };
};
